<script setup lang="ts">
    import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
    import { cn } from '@/lib/utils';
    import type { Tab } from '@/types';

    interface Props {
        tabs: Tab[];
        defaultTab?: string;
        containerClass?: string;
        tabListClass?: string;
        tabTriggerClass?: string;
        tabContentClass?: string;
    }

    withDefaults(defineProps<Props>(), {
        defaultTab: '',
    });
</script>

<template>
    <TabsRoot
        :class="cn('flex w-full flex-col', containerClass)"
        :default-value="defaultTab || tabs[0]?.value"
    >
        <TabsList
            :class="
                cn(
                    'no-scrollbar sticky top-0 z-20 mb-5 flex shrink-0 gap-5 overflow-x-auto overflow-y-hidden border-b bg-white',
                    tabListClass,
                )
            "
        >
            <TabsIndicator
                class="bg-primary absolute bottom-0 left-0 h-[2px] w-[var(--reka-tabs-indicator-size)] translate-x-[var(--reka-tabs-indicator-position)] rounded-full transition-[width,transform,translate] duration-300"
            />

            <TabsTrigger
                v-for="tab in tabs"
                :key="tab.value"
                :value="tab.value"
                :class="
                    cn(
                        'text-primary/50 hover:text-primary data-[state=active]:text-primary flex cursor-pointer items-center justify-center py-3 text-xs leading-none whitespace-nowrap outline-none select-none focus-visible:relative focus-visible:shadow-[0_0_0_2px] focus-visible:shadow-black',
                        tabTriggerClass,
                    )
                "
            >
                {{ $t(tab.label) }}
            </TabsTrigger>
        </TabsList>

        <TabsContent
            v-for="tab in tabs"
            :key="tab.value"
            :value="tab.value"
            :class="cn('grow outline-none', tabContentClass)"
        >
            <slot :name="tab.value" />
        </TabsContent>
    </TabsRoot>
</template>
